import { Children, FC, ReactNode, isValidElement } from "react";

import { BaseProps } from "../../BaseProps";
import { getHtmlPropsFromBaseProps } from "../../utilities/getHtmlPropsFromBaseProps";
import { withSubComponents } from "../../utilities/withSubComponents";
import { Group } from "./Group";
import { Item } from "./Item";

import legacyStyles from "./legacy/styles.module.scss";
import tokenStyles from "./styles.module.css";
import { createComponentStyleHook } from "../../tokens";

/** The `SidebarNav` component properties. */
export interface SidebarNavProps extends BaseProps {
  /** The component children. */
  readonly children?: ReactNode;

  /** The accessible label for the navigation region. */
  readonly ariaLabel?: string;
}

/** The `SidebarNav` sub components. */
export interface SideBarNavSubComponents {
  /** Represents a group of items in the sidebar. */
  readonly Group: typeof Group;

  /** Represents an item within the sidebar. */
  readonly Item: typeof Item;
}

const useStyles = createComponentStyleHook({
  legacyStyles,
  tokenStyles,
  componentTokens: "sidebar_nav",
});

/** The SideBar navigation component. */
export const SidebarNav = withSubComponents<FC<SidebarNavProps>, SideBarNavSubComponents>(
  {
    Group,
    Item,
  },
  (props) => {
    const { children, ariaLabel = "Sidebar navigation" } = props;
    const { mode, styles, StyleModeBoundary } = useStyles(props);

    const baseHtmlProps = getHtmlPropsFromBaseProps(props, {
      className: [styles.SidebarNav, "sidebar-nav"],
    });

    const validElements = Children.map(children, (child) => {
      if (isValidElement(child)) return child;
      return null;
    })?.filter((child) => !!child);

    return (
      <StyleModeBoundary mode={mode}>
        <nav {...baseHtmlProps} aria-label={ariaLabel}>
          <ul>
            {validElements?.map((element, index) => {
              let hasSeparator = index < validElements.length - 1;
              const nextItem = validElements[index + 1];

              if (element.type === Item && nextItem?.type === Item) hasSeparator = false;

              return (
                <li key={index}>
                  {element}
                  {hasSeparator && <div className={styles.separator} aria-hidden="true" />}
                </li>
              );
            })}
          </ul>
        </nav>
      </StyleModeBoundary>
    );
  },
);
